import { useTweaks } from '../components/Tweaks.jsx';
import { Header } from '../components/Header.jsx';
import { Footer } from '../components/Blocks.jsx';
import { CelebritiesStrip } from '../components/Testimonials.jsx';
import {
  TeachersHero, TierLadder, TeachersSampleAndPricing, TeachersFAQ, FooterCTA,
} from '../components/TeachersOnline.jsx';
import {
  ResultsEvidence, TheAnswer, Mechanism, InAnyLanguage, CleanConscience,
  BuiltByTeachers, FoundingTeachers, FirstWeek,
} from '../components/TeachersOnlineAudience.jsx';

// Copy is the source of truth from Aran's 2026-07-13 redraft
// (gwefan/updates/tutors.md). Commercial model unchanged: 30 days free,
// no card → students subscribe via the tutor's link at £10/mo → tutor
// keeps £5 → £15/mo after the trial (break-even 3 students).
//
// Redraft decisions carried into this build:
// - Pain section, invented teacher testimonials (Maria/James/Sophie) and
//   the "Most teachers" pill are CUT (no data behind them).
// - Cardiff study honestly framed (children, Welsh, schools — same
//   engine), copy aligned with the schools pages' research feature,
//   including their "Read the published study" link text.
// - Built-by-teachers aligned with the schools pages: Tom + Aran with
//   team photos, Catrin's card and the "50+ years" stat cut.
// - toWeek1Step3 uses the fallback wording (send your link directly):
//   the student-facing landing page it could promise doesn't exist yet.
//   When Tom builds it, upgrade the step to point at it.
// - Payout mechanics ("we pay you out monthly") match the maths
//   footnote; confirm against what Ivan/Tom actually build — same
//   standing flag as schools pricing.
const TWEAK_DEFAULTS = {
  navWelshResources: "Welsh Resources",
  navCommunity: "Community",
  navAboutUs: "About us",
  navBlog: "Blog",
  navContact: "Contact",
  login: "Login",
  startLearning: "Start Learning",

  // --- hero ---
  toKicker: "For freelance English teachers",
  toHeadline: "Be the teacher whose students actually speak.",
  toSub: "Teach with the SaySomethingin Method and your students speak more, improve faster, and stay with you longer. Then sell them daily practice between lessons – and earn from every one.",
  toSubL1: "You teach in English; your students practise between lessons in their own language. They subscribe through your link at £10 a month (a 33% discount) and you keep £5 of every payment.",
  toCta: "Start 30 days free",
  toCta2: "How it works",
  toTrust: "No card details needed. Free for 30 days – long enough to prove it in your lessons and be in profit before you ever pay.",
  toBadgeAmount: "15",
  toBadgeLabel: "/ month after your free trial. Three students makes it free for you.",

  // --- results & evidence (replaces the pain section) ---
  toResKicker: "Start with results",
  toResTitle: "Students who speak every day feel themselves improving. Students who feel themselves improving stay.",
  toResBody1: "Every tutor knows the real threat to their income: not their rate, but the quiet goodbye. Students drift away when progress stops feeling real – and once-a-week lessons, however good, make progress hard to feel. Daily spoken practice changes that. Your students say English out loud every day, arrive at your lesson noticeably faster and more willing to speak, and the improvement is something they can hear in their own voice.",
  toResBody2: "That's the engine of everything on this page. Better results keep your students coming back and get them to recommend you to their friends (which is even more important than the £5 a month).",
  toResearchKicker: "Independently evaluated – Cardiff University",
  toResearchTitle: "The evidence comes from Wales. The method is the same.",
  toResearchBody: "In 2024, Cardiff University ran an independent study of SaySomethingin in schools in Wales. In the classes without it, almost every pupil scored in the bottom 10% of possible marks. In the classes using it, half the pupils scored in the top 10% – from as little as five minutes a week. The study was children learning Welsh; your students are adults learning English. The engine is identical: the same loop, the same brain science, the same voices in the learner's ears.",
  toProofResearchLink: "Read the published study",
  toResearchQuote: "Some of the most successful learners with SaySomethingin are not the learners usually recognised as academically strong.",
  toResearchCite: "– Cardiff University report, 2024",

  // --- the answer (merged with the old week view) ---
  toAnsKicker: "Here's the answer",
  toAnsTitle: "What changes isn't your lessons. It's the six days in between.",
  toAnsLede: "You keep doing what you do. Your students go from seeing you once or twice a week to practising out loud every day – and arriving ready to use what they've learned. They improve faster, they stay longer, and they pay a small monthly subscription through you to do it.",
  toWeekDay1: "Mon",
  toWeekDay2: "Tue",
  toWeekDay3: "Wed",
  toWeekDay4: "Thu",
  toWeekDay5: "Fri",
  toWeekDay6: "Sat",
  toWeekDay7: "Sun",
  toWeekLessonLabel: "Your lesson",
  toWeekSsiLabel: "SSi daily practice",
  toWeekFreedom: "You choose how much time to give each student. One-to-ones, small group calls, or none at all – your students progress either way, and you earn from every one.",
  toAnsTag1: "Results that retain",
  toAnsT1: "Students who improve don't drift away.",
  toAnsB1: "Daily speaking is progress students can hear in their own voice. That's what keeps them booking lessons, month after month – and telling their friends who taught them.",
  toAnsTag2: "Your prep, done",
  toAnsT2: "Walk into every lesson with a student who's been practising all week.",
  toAnsB2: "No more one-use worksheets at 10pm. Your prep is pressing play. The method handles repetition and drilling; you handle correction, nuance, and the conversation they can only have with you.",
  toAnsTag3: "A second income",
  toAnsT3: "Earn from every student, every month.",
  toAnsB3: "Students subscribe through your link at £10 a month and you keep £5 of every one. Sell ten and that's £50 a month on top of your lesson income – for recommending something that makes your teaching look better, not worse.",
  toAnsCloser: "Same lessons. Same hours. Six extra days a week of progress for your students, and a second income stream for you.",

  // --- what actually happens (the mechanism) ---
  toMechKicker: "What actually happens",
  toMechTitle: "Five minutes, out loud, every day.",
  toMechLede: "No worksheets, no vocab lists, no grammar tables. A session is a simple loop your student runs on their phone, and it has them producing spoken English from the first minute.",
  toMechStep1T: "Hear it.",
  toMechStep1B: "A voice asks, in their own language: “Say ‘I want to speak.’”",
  toMechStep2T: "Reach for it.",
  toMechStep2B: "A pause. Their brain works to build the English – and that effort is exactly what makes it stick.",
  toMechStep3T: "Say it.",
  toMechStep3B: "They say it out loud. Wrong is fine; wrong then corrected is how the brain wires it in.",
  toMechStep4T: "Hear it back.",
  toMechStep4B: "Two English speakers say the phrase, and the student hears instantly how close they got.",
  toMechMaths: "The loop repeats, and the phrases grow and combine. In a conversation lesson, your student speaks for a fraction of the hour. In a SaySomethingin session, they're speaking the whole time – so five minutes a day between your lessons can mean more spoken English than the lessons themselves have room for.",
  toMechCloser: "Speaking is a physical skill, like swimming. Reading about it never made anyone a swimmer – and that's why your students will feel the difference inside their first session.",

  // --- in any language (the unlock) ---
  toLangKicker: "The bit you can't do yourself",
  toLangTitle: "Teach absolute beginners – in any language.",
  toLangLede: "Most online English teachers can only really help students who already speak some English. SaySomethingin changes that. Your students learn English in their own language – Mandarin, Spanish, Arabic, and more – so absolute beginners arrive at your lesson already speaking. You don't need to learn twenty languages. SSi does that part for you.",
  toLangPoint1H: "Unlocks absolute beginners.",
  toLangPoint1B: "A segment of students you could never profitably help before – and the students with the longest journeys ahead of them, which makes them the students who stay the longest.",
  toLangPoint2H: "Confidence from day one.",
  toLangPoint2B: "No one is drowning in a foreign language with a foreign teacher. They've already said it out loud a hundred times on their own time before your lesson starts.",
  toLangPoint3H: "You keep teaching in English.",
  toLangPoint3B: "Your lesson doesn't change – the conversation, correction and nuance you bring is exactly what English-only teaching is built for. Their prep finally makes sense.",
  toLangChipsLabel: "Available source languages",
  toLangMandarin: "Mandarin",
  toLangSpanish: "Spanish",
  toLangFrench: "French",
  toLangGerman: "German",
  toLangItalian: "Italian",
  toLangPortuguese: "Portuguese",
  toLangArabic: "Arabic",
  toLangJapanese: "Japanese",
  toLangKorean: "Korean",
  toLangHindi: "Hindi",
  toLangBengali: "Bengali",
  toLangGujarati: "Gujarati",
  toLangPunjabi: "Punjabi",
  toLangTamil: "Tamil",
  toLangUrdu: "Urdu",
  toLangMarathi: "Marathi",
  toLangTelugu: "Telugu",
  toLangMore: "…and more coming soon",

  // --- the maths ---
  toMathsKicker: "The maths",
  toMathsTitle: "Every student is £5 a month in your pocket.",
  toMathsLede: "Your students subscribe at £10 a month through your link – a discount on our usual price. Half is yours. Your own £15 subscription pays for itself at three students; everyone after that is profit.",
  toMathsTrial: "And your first 30 days are free. Prove it in your lessons, sign up your first three students, and you can be in profit before your first payment ever leaves your account.",
  toMathsStep1Label: "Your student pays",
  toMathsStep1Big: "£10 / mo",
  toMathsStep1Sub: "through your link – a discount on our usual price",
  toMathsStep2Label: "You keep",
  toMathsStep2Big: "£5",
  toMathsStep2Sub: "of every student, every month*",
  toMathsStep3Label: "Your subscription",
  toMathsStep3Big: "£15 / mo",
  toMathsStep3Sub: "after 30 days free – three students makes it free for you",
  toMathsFootnote: "* We collect payment on your behalf and pay you out monthly. Your £5 share is before any tax and payment-processing charges. The other half covers our costs and the platform.",
  toMathsProofStat: "3 students",
  toMathsProofBody: "Break-even. Three students at £10 a month covers your £15 subscription – everyone after that is profit. Bring ten and you've added £50 a month, for no extra work (and for less preparation, if you choose).",

  // --- recommend it with a clean conscience ---
  toTrustKicker: "Before you put your name on it",
  toTrustTitle: "You're recommending this to your own students. So here's how we treat them.",
  toTrustBody1: "No streaks, no owls, no shame loops, no dark patterns at cancellation. Your students can leave with one click and keep access to the end of their billing cycle. We only want them to stay because it's working.",
  toTrustBody2: "And you should try it before you ever mention it to a student. Learn a language you don't speak for half an hour, free, right now – if you don't feel the method working on you, then don't tell your students about it.",
  toTrustCta: "Try a free session yourself",

  // --- built by teachers (aligned with the schools pages) ---
  toBuiltKicker: "Built by teachers",
  toBuiltTitle: "Shaped by people who've been in the room.",
  toBuiltBody: "SaySomethingin for online teachers was built by Tom and Aran, who are passionate classroom teachers. Every piece of this, from the lesson flow to the way students learn between sessions, was designed by people who've lived it.",
  toBuiltName1: "Tom",
  toBuiltRole1: "Physics & Maths teacher",
  toBuiltImg1: "/assets/team/tom-c.jpg",
  toBuiltName2: "Aran",
  toBuiltRole2: "English & Welsh teacher",
  toBuiltImg2: "/assets/team/aran-j.jpg",

  // --- founding teachers (replaces invented testimonials) ---
  toFirstKicker: "Founding teachers",
  toFirstTitle: "Be one of the first teachers to build on it.",
  toFirstBody1: "The teacher programme is new, and we'd rather tell you that than invent testimonials. Founding teachers get a direct line to our team, first say in what we build next, and the best possible deal on being early: thirty days free, no card, and students who feel the difference in week one.",
  toFirstBody2: "What we can already show you: an independent Cardiff University study, a method with over 100,000 learners since 2009, and a free half-hour that lets you judge it yourself before your name goes anywhere near it.",
  toFirstCta: "Start 30 days free",

  // --- your first week (the on-ramp) ---
  toWeek1Kicker: "Getting started",
  toWeek1Title: "From signup to first payout.",
  toWeek1Step1T: "Day one.",
  toWeek1Step1B: "Sign up free, run a session yourself, and set up your referral link.",
  toWeek1Step2T: "Your next lessons.",
  toWeek1Step2B: "Run five minutes of a session live with each student – on your screen or theirs. That's the sales pitch; the method makes it for you.",
  toWeek1Step3T: "When they're in.",
  toWeek1Step3B: "Send them your link. They subscribe directly with us at £10 a month – you never have to handle the money.",
  toWeek1Step4T: "Every month.",
  toWeek1Step4B: "We collect payments and pay out your share monthly. No invoicing, no chasing.",
  toWeek1Closer: "All of that fits inside your free 30 days. Do it, and you can be in profit before your subscription even starts.",

  // --- sample + pricing ---
  toSampleKicker: "Try it yourself",
  toSampleTitle: "Try the method before your students do.",
  toSampleLede: "Press play and learn for up to half an hour. Pick a language you don't speak – you'll understand exactly what your students are going to experience, and why they'll come back for more.",
  toSamplePickerLabel: "Choose your language",
  toPricingKicker: "Pricing",
  toPricingTitle: "Simple pricing, real income.",
  toPricingLede: "Start free for 30 days. Keep going for £15 a month – and earn £5 from every student you bring.",
  toPricingMonthly: "Monthly",
  toPricingAnnual: "Annual",
  toPricingPlanLabel: "Teacher subscription",
  toPricingAnnualEffective: "£12.50/month, billed yearly",
  toPricingMonthlyEffective: "Cancel anytime",
  toPricingFeat1: "Teach with SaySomethingin in your 1:1s and classes",
  toPricingFeat2: "Your students subscribe at £10/mo through your link",
  toPricingFeat3: "You keep £5 of every student, every month*",
  toPricingFeat4: "Class accounts for group sessions",
  toPricingFeat5: "30 days free – no card details to start",
  toPricingCta: "Start 30 days free",
  toPricingTrial: "Free for 30 days, no card details. Then £15 a month – three students makes it free for you.",

  // --- FAQ ---
  toFaqKicker: "Teacher questions",
  toFaqTitle: "The things you actually want to know.",
  toFaqLede: "Money, mechanics, the small print. If there's anything else you'd like to ask, email us and we'll come back within a working day.",
  toFaqContact: "Email the teacher team →",
  toFaqQ1: "Can I cancel any time?",
  toFaqA1: "Yes, with one click.",
  toFaqQ2: "What happens to my students' accounts if I cancel?",
  toFaqA2: "They keep full access through to the end of the current billing cycle, then they can continue directly with us or move on. You don't hold their language learning hostage - that would be weird.",
  toFaqQ3: "Is the app branded as mine or as SaySomethingin?",
  toFaqA3: "As SaySomethingin. You're the referring teacher, and students know it - but the product is ours, the support is ours, and the methodology is ours. You don't inherit any of that overhead.",
  toFaqQ4: "Which languages are included?",
  toFaqA4: "All ten of our paid languages - Spanish, French, German, Italian, Portuguese, Mandarin, Japanese, Korean, Arabic, and Welsh. Teach English with it today, sell accounts for any other language tomorrow.",
  toFaqQ5: "Do I need any tech to use it in a live lesson?",
  toFaqA5: "Whatever you already have. A phone, a tablet, a Chromebook. Cast to a screen if you have one, or have students gather round a device. No projector, no special kit.",
  toFaqQ6: "How do students pay, and when do I get paid?",
  toFaqA6: "Students subscribe directly with us at £10 a month through your referral link. We pay you £5 for every one each month (before tax and payment-processing charges). No invoicing, no chasing.",
  toFaqQ7: "What if a student already has a SaySomethingin account?",
  toFaqA7: "They can link their existing account to you. You won't earn from their historic subscription, but you'll earn £5 a month going forward once they're on your link.",
  toFaqQ8: "Is there a minimum commitment?",
  toFaqA8: "No. Cancel any time. We only want you to stay if you're making a profit (but even then, you can cancel if you want to!).",
  toFaqQ9: "Has this been used by English teachers before?",
  toFaqA9: "You'd be among the first, and we'll be working particularly closely with the teachers who are. The method itself has over 100,000 learners since 2009 and an independent Cardiff University study behind it - the teacher programme is the new part, and founding teachers get to shape it.",

  // --- footer CTA ---
  toFooterCtaTitle: "Try it on yourself first. Then decide.",
  toFooterCtaSub: "Thirty days free is enough time to learn some of a language yourself, run it live in a few lessons, and sign up your first students - you can be in profit before you've paid a penny. If it doesn't click, walk away - no card, no fuss.",
  toFooterCtaBtn: "Start your 30 days free →",

  // --- celebrities strip (shared component) ---
  celebKicker: "Also learning with SSi",
  celebTitle: "From rugby pitches to Countdown.",
  celebSub: "Celebrities who've worked with the SaySomethingin methodology author Aran.",
  celebLeadIn: "The method your students will be learning is the same one these people chose for high-pressure performance.",
  celebMoreLink: "See all celebrity testimonials →",

  // --- shared chrome ---
  footerAbout: "About",
  footerCareers: "Careers",
  footerContact: "Contact",
  footerHelp: "Help centre",
  footerTerms: "Terms",
  footerPrivacy: "Privacy",
  footerAppHeading: "Download",
  footerAppApple: "iOS",
  footerAppGoogle: "Android",
  footerLivingWage: "We are a Living Wage Employer",
  footerCopyright: "© 2025 SaySomethingin Cyf",
  footerCol1: "The product",
  footerCol2: "Community",
  footerCol3: "Download",
  footerLegal: "© 2026 SaySomethingIn Cyf. Glaslyn, Ffordd y Parc, Bangor, Gwynedd LL57 4FE, Wales.",
};

const GROUPS = [
  { name: 'Hero', keys: ['toKicker','toHeadline','toSub','toSubL1','toCta','toCta2','toTrust','toBadgeAmount','toBadgeLabel'], long:['toHeadline','toSub','toSubL1','toTrust','toBadgeLabel'] },
  { name: 'Results & evidence', keys: ['toResKicker','toResTitle','toResBody1','toResBody2','toResearchKicker','toResearchTitle','toResearchBody','toProofResearchLink','toResearchQuote','toResearchCite'], long:['toResTitle','toResBody1','toResBody2','toResearchTitle','toResearchBody','toResearchQuote'] },
  { name: "The answer", keys: ['toAnsKicker','toAnsTitle','toAnsLede','toWeekDay1','toWeekDay2','toWeekDay3','toWeekDay4','toWeekDay5','toWeekDay6','toWeekDay7','toWeekLessonLabel','toWeekSsiLabel','toWeekFreedom','toAnsTag1','toAnsT1','toAnsB1','toAnsTag2','toAnsT2','toAnsB2','toAnsTag3','toAnsT3','toAnsB3','toAnsCloser'], long:['toAnsTitle','toAnsLede','toWeekFreedom','toAnsT1','toAnsB1','toAnsT2','toAnsB2','toAnsT3','toAnsB3','toAnsCloser'] },
  { name: 'Mechanism', keys: ['toMechKicker','toMechTitle','toMechLede','toMechStep1T','toMechStep1B','toMechStep2T','toMechStep2B','toMechStep3T','toMechStep3B','toMechStep4T','toMechStep4B','toMechMaths','toMechCloser'], long:['toMechLede','toMechStep1B','toMechStep2B','toMechStep3B','toMechStep4B','toMechMaths','toMechCloser'] },
  { name: 'In any language', keys: ['toLangKicker','toLangTitle','toLangLede','toLangPoint1H','toLangPoint1B','toLangPoint2H','toLangPoint2B','toLangPoint3H','toLangPoint3B','toLangChipsLabel','toLangMandarin','toLangSpanish','toLangFrench','toLangGerman','toLangItalian','toLangPortuguese','toLangArabic','toLangJapanese','toLangKorean','toLangHindi','toLangBengali','toLangGujarati','toLangPunjabi','toLangTamil','toLangUrdu','toLangMarathi','toLangTelugu','toLangMore'], long:['toLangTitle','toLangLede','toLangPoint1B','toLangPoint2B','toLangPoint3B'] },
  { name: 'The maths', keys: ['toMathsKicker','toMathsTitle','toMathsLede','toMathsTrial','toMathsStep1Label','toMathsStep1Big','toMathsStep1Sub','toMathsStep2Label','toMathsStep2Big','toMathsStep2Sub','toMathsStep3Label','toMathsStep3Big','toMathsStep3Sub','toMathsFootnote','toMathsProofStat','toMathsProofBody'], long:['toMathsTitle','toMathsLede','toMathsTrial','toMathsStep1Sub','toMathsStep2Sub','toMathsStep3Sub','toMathsFootnote','toMathsProofBody'] },
  { name: 'Clean conscience', keys: ['toTrustKicker','toTrustTitle','toTrustBody1','toTrustBody2','toTrustCta'], long:['toTrustTitle','toTrustBody1','toTrustBody2'] },
  { name: 'Built by teachers', keys: ['toBuiltKicker','toBuiltTitle','toBuiltBody','toBuiltName1','toBuiltRole1','toBuiltImg1','toBuiltName2','toBuiltRole2','toBuiltImg2'], long:['toBuiltTitle','toBuiltBody'] },
  { name: 'Celebrities strip', keys: ['celebKicker','celebTitle','celebSub','celebLeadIn','celebMoreLink'], long:['celebSub','celebLeadIn'] },
  { name: 'Founding teachers', keys: ['toFirstKicker','toFirstTitle','toFirstBody1','toFirstBody2','toFirstCta'], long:['toFirstTitle','toFirstBody1','toFirstBody2'] },
  { name: 'Your first week', keys: ['toWeek1Kicker','toWeek1Title','toWeek1Step1T','toWeek1Step1B','toWeek1Step2T','toWeek1Step2B','toWeek1Step3T','toWeek1Step3B','toWeek1Step4T','toWeek1Step4B','toWeek1Closer'], long:['toWeek1Step1B','toWeek1Step2B','toWeek1Step3B','toWeek1Step4B','toWeek1Closer'] },
  { name: 'Sample', keys: ['toSampleKicker','toSampleTitle','toSampleLede','toSamplePickerLabel'], long:['toSampleTitle','toSampleLede'] },
  { name: 'Pricing', keys: ['toPricingKicker','toPricingTitle','toPricingLede','toPricingMonthly','toPricingAnnual','toPricingPlanLabel','toPricingAnnualEffective','toPricingMonthlyEffective','toPricingFeat1','toPricingFeat2','toPricingFeat3','toPricingFeat4','toPricingFeat5','toPricingCta','toPricingTrial'], long:['toPricingLede','toPricingTrial'] },
  { name: 'FAQ', keys: ['toFaqKicker','toFaqTitle','toFaqLede','toFaqContact','toFaqQ1','toFaqA1','toFaqQ2','toFaqA2','toFaqQ3','toFaqA3','toFaqQ4','toFaqA4','toFaqQ5','toFaqA5','toFaqQ6','toFaqA6','toFaqQ7','toFaqA7','toFaqQ8','toFaqA8','toFaqQ9','toFaqA9'], long:['toFaqLede','toFaqA1','toFaqA2','toFaqA3','toFaqA4','toFaqA5','toFaqA6','toFaqA7','toFaqA8','toFaqA9'] },
  { name: 'Footer CTA', keys: ['toFooterCtaTitle','toFooterCtaSub','toFooterCtaBtn'], long:['toFooterCtaTitle','toFooterCtaSub'] },
];

export default function TeachersOnline() {
  const { tweaks, panel } = useTweaks({ defaults: TWEAK_DEFAULTS, groups: GROUPS });
  return (
    <div data-screen-label="Teachers Online">
      <Header t={tweaks}/>
      <main>
        <TeachersHero t={tweaks}/>
        <ResultsEvidence t={tweaks}/>
        <TheAnswer t={tweaks}/>
        <Mechanism t={tweaks}/>
        <InAnyLanguage t={tweaks}/>
        <TierLadder t={tweaks}/>
        <CleanConscience t={tweaks}/>
        <BuiltByTeachers t={tweaks}/>
        <CelebritiesStrip t={tweaks}/>
        <FoundingTeachers t={tweaks}/>
        <FirstWeek t={tweaks}/>
        <TeachersSampleAndPricing t={tweaks}/>
        <TeachersFAQ t={tweaks}/>
        <FooterCTA t={tweaks}/>
      </main>
      <Footer t={tweaks} hideCta={true}/>
      {panel}
    </div>
  );
}
